/** @jsx jsx */
import React, { useState, useEffect, useCallback } from 'react';
import { jsx, css } from '@emotion/react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import { notify } from '../../util/notifications';
import SearchInvoice from './SearchInvoice';
import SearchCriteria from '../../components/dispatchComponents/invoiceSearch/search/searchCriteria';
import { getSearchInvoiceList, clearSearchData, getChildList } from '../../actions/dispatchAction/invoiceSearchAction';
import { getAccessLevelsByModuleName, isManagement } from '../../util/customMethods';

/**
 * InvoiceSearch (v2)
 *
 * Changes:
 *  - Functional component (no componentWillMount)
 *  - Loading state during search
 *  - Fixed broken unauthorized HTML
 *  - Immutable state updates for search criteria
 *  - clearSearchData is now synchronous dispatch
 */

const PAGE_TITLE = 'Invoice Details';

// ── Styles ──────────────────────────────────────────────────────────────────

const pageHeader = css`
    margin-bottom: 12px;
    h4 { font-size: 1.15rem; font-weight: 600; color: #2c3e50;
         display: flex; align-items: center; gap: 8px; margin: 0;
         i { color: #5a8bb5; } }
`;

const unauthorizedBox = css`
    text-align: center; padding: 40px 20px;
    h3 { color: #dc3545; font-size: 1.25rem; }
`;

const loadingBar = css`
    height: 3px; background: linear-gradient(90deg, #5a8bb5 30%, #e0e0e0 50%, #5a8bb5 70%);
    background-size: 200% 100%; animation: shimmer 1.5s infinite;
    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;

// ── Component ───────────────────────────────────────────────────────────────

const InvoiceSearch = ({ dispatch, refNotify, searchInvoiceListData, childCompList }) => {
    const isMgmt = isManagement();

    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [customInvoiceValues, setCustomInvoiceValues] = useState([
        { optionTxt: 'InvoiceNumber', optionVal: '' },
    ]);
    const [loading, setLoading] = useState(false);

    // ── Init + cleanup ──────────────────────────────────────────────────
    useEffect(() => {
        document.title = PAGE_TITLE;
        if (isMgmt) dispatch(getChildList());
        dispatch(clearSearchData());
        return () => { dispatch(clearSearchData()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Search ──────────────────────────────────────────────────────────
    const handleSearch = useCallback(async (criteria, companyId) => {
        const jsonObject = {
            fromDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : '',
            toDate: toDate ? moment(toDate).format('YYYY-MM-DD') : '',
        };
        criteria.forEach((el) => { jsonObject[el.optionTxt] = el.optionVal; });
        if (companyId) jsonObject.companyId = companyId;

        setLoading(true);
        const res = await dispatch(getSearchInvoiceList(JSON.stringify(jsonObject)));
        setLoading(false);

        if (res.statusCode === 200) {
            if (res.response && res.response.length === 1) {
                window.open('/dispatch/serviceCallInfo/' + res.response[0].invoiceId, '_blank');
            }
        } else {
            notify.createNotification('error', res.statusMsg, refNotify);
        }
    }, [dispatch, fromDate, toDate, refNotify]);

    // ── Field handlers ──────────────────────────────────────────────────
    const handleDateChange = useCallback((key, value) => {
        if (key === 'fromDate') setFromDate(value);
        if (key === 'toDate') setToDate(value);
    }, []);

    const handleCriteriaFieldChange = useCallback((e, index, type) => {
        setCustomInvoiceValues((prev) => {
            const next = prev.map((item, i) => {
                if (i !== index) return item;
                return type === 'T'
                    ? { ...item, optionTxt: e.target.value }
                    : { ...item, optionVal: e.target.value };
            });
            return next;
        });
    }, []);

    const addCriteria = useCallback(() => {
        setCustomInvoiceValues((prev) => [...prev, { optionTxt: '', optionVal: '' }]);
    }, []);

    const removeCriteria = useCallback((index) => {
        setCustomInvoiceValues((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    const handleEnterKey = useCallback((e, req, companyId) => {
        if (e.which === 13) handleSearch(req, companyId);
    }, [handleSearch]);

    // ── Permissions ─────────────────────────────────────────────────────
    const dashboard = (getAccessLevelsByModuleName('Search Existing Call')[0]) || {};
    if (!dashboard.basicAccess || !dashboard.basicAccess.view) {
        return (
            <div className="pd-10 bg-white border-radius-4 box-shadow container-box" css={unauthorizedBox}>
                <h3>Not Authorised User</h3>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header" css={pageHeader}>
                <div className="row mb-10">
                    <div className="col-md-12 col-sm-12">
                        <h4><i className="fa fa-id-card" /> Invoice Search Screen</h4>
                    </div>
                </div>
            </div>

            <div className="row mb-10">
                <div className="col-md-12 col-sm-12">
                    <SearchCriteria
                        childCompList={childCompList}
                        onDateFieldsChange={handleDateChange}
                        onenterKeyPress={handleEnterKey}
                        onInvoiceTyoeChange={handleCriteriaFieldChange}
                        addSearchCriteria={addCriteria}
                        removeSearchCriteria={removeCriteria}
                        onCallActionBtnClick={handleSearch}
                        fromDate={fromDate}
                        toDate={toDate}
                        customVal={customInvoiceValues}
                    />
                </div>
            </div>

            {loading && <div css={loadingBar} />}

            <div className="pd-10 bg-white border-radius-4 box-shadow container-box">
                <SearchInvoice data={searchInvoiceListData} />
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    refNotify: state.refNotify,
    authInfo: state.authInfo,
    searchInvoiceListData: state.searchInvoiceList.searchInvoiceList,
    childCompList: state.searchInvoiceList.childComList,
});

export default withRouter(connect(mapStateToProps)(InvoiceSearch));
